import { createRequestOptions } from './request-options';
import { createRequest } from './request';

export class Client<paths extends Api.PathsAbstract, UseExt extends Api.Ext.UseExt> {

	readonly options: Readonly<Api.ClientOptions>;
	readonly useExt: UseExt;
	readonly middlewares: Api.Middleware[] = [];
	abortControllers: Record<string, AbortController> = {};

	constructor(clientOptions: Partial<Api.ClientOptions> = {}, useExt: UseExt) {
		clientOptions.requestOptions = createRequestOptions(clientOptions.requestOptions);
		clientOptions.useApiConsole ??= true;

		clientOptions.fetch ??= globalThis.fetch;
		clientOptions.Request ??= globalThis.Request;
		clientOptions.querySerializer ??= async (params) => {
			const { querySerializer } = await import('../utils/lazy');

			return querySerializer(params);
		}
		clientOptions.bodySerializer ??= (body) => typeof body === 'string' ? body : JSON.stringify(body);

		this.options = clientOptions as Api.ClientOptions;

		this.useExt = useExt;
	}

	generateUUID() {
		if (crypto?.randomUUID) {
			return crypto.randomUUID();
		}

		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
			const r = Math.random() * 16 | 0;
			const v = c === 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	/**
	 * Зарегистрировать middleware
	 */
	use(middleware: Api.Middleware) {
		this.middlewares.push(middleware);
	}

	/**
	 * Создать объект запроса
	 *
	 * После создания объекта он заполняется необходимыми данными и выполняет запросы к API с помощью APIRequest2.call();
	 *
	 * Опционально generic указывать нельзя, поэтому необходимо дублировать определение fetchStyle, [см.](https://github.com/Microsoft/TypeScript/issues/26242)
	 *
	 * @param path Путь метода API
	 * @param fieldsNarrow Список получаемых полей, для методов API с моделью. Если объект запроса переиспользуется, и список полей меняется, указывайте его непосредственно при вызове `api.call()`
	 */
	gen<
		Path extends Api.Path<paths>,
		FieldsNarrow extends Api.ParamsFields<Api.ClientRequestContext<paths, Path, UseExt>>
	>(
		path: Path,
		fieldsNarrow?: FieldsNarrow,
	): Api.ClientRequest<Api.ClientRequestContext<paths, Path, UseExt>, FieldsNarrow> {
		type RequestContext = Api.ClientRequestContext<paths, Path, UseExt>;

		return createRequest<RequestContext, FieldsNarrow>(this, path, fieldsNarrow);
	}

	/**
	 * Создать объект запроса, не описанного в OpenAPI
	 */
	genAny<
		Params extends Record<string, any>,
		Result = any,
		FieldsNarrow extends Api.Ext.Schemas['Fields'] | undefined = undefined
	>(path: Api.PathAbstract, fieldsNarrow?: FieldsNarrow): Api.ClientRequestCustom<UseExt, Params, Result> {
		return this.gen(path as any, fieldsNarrow as any) as unknown as Api.ClientRequestCustom<UseExt, Params, Result>;
	}

	/**
	 * Создать контекст выполнения запроса
	 */
	genMiddlewareCtx(
		clientRequest: Api.ClientRequest<any, any, any>,
		data: Record<string, any>,
	): Omit<Api.MiddlewareCallbackParams, 'request'> {
		const id = this.generateUUID();

		const options: Api.BaseOptions = Object.freeze({
			baseUrl: this.options.baseUrl,
			querySerializer: this.options.querySerializer,
			bodySerializer: this.options.bodySerializer,
			Request: this.options.Request,
			fetch: this.options.fetch,
		});

		return {
			useExt: this.useExt,
			requestOptions: clientRequest.options,
			data,
			middlewareData: {},
			options,
			schemaPath: clientRequest.path,
			id,
		};
	};

	/**
	 * Обработчик middleware.onRequest
	 *
	 * @param request Запрос для обработки
	 * @param middlewareCtx Контекст выполнения запроса
	 * @return Новый экземпляр `Request` или `Response` при модификации запроса, или undefined
	 */
	async middlewareOnRequest(
		request: Request,
		middlewareCtx: Api.MiddlewareCallbackParams,
	): Promise<Request | Response | undefined> {
		for (const middleware of this.middlewares) {
			const result = await middleware.onRequest?.({
				...middlewareCtx,
				request,
			});

			if (result) {
				if (result instanceof Request) {
					request = result;
				} else if (result instanceof Response) {
					return result;
				} else {
					throw new Error('onRequest: must return new Request() or Response() when modifying the request');
				}
			}
		}

		return request;
	}

	/**
	 * Обработчик middleware.onResponse
	 *
	 * @param `error` Ошибка, переданная для обработки
	 * @param middlewareCtx
	 * @return Новый экземпляр `Response` при успешной обработке или выбрасывает ошибку
	 */
	async middlewareOnError(
		error: Error | unknown,
		middlewareCtx: Api.MiddlewareCallbackParams,
	): Promise<Response | never> {
		let errorAfterMiddleware = error;

		for (const middleware of this.middlewares) {
			const result = await middleware.onError?.({
				...middlewareCtx,
				error: errorAfterMiddleware,
			});

			if (result) {
				if (result instanceof Response) {
					return result;
				}

				if (result instanceof Error) {
					errorAfterMiddleware = result;

					continue;
				}

				throw new Error('onError: must return new Response() or instance of Error');
			}
		}

		throw errorAfterMiddleware;
	}

	/**
	 * Обработчик middleware.onResponse
	 *
	 * @param response Ответ для обработки
	 * @param middlewareCtx Контекст выполнения запроса
	 * @return Новый экземпляр `Response` при модификации ответа, или undefined
	 */
	async middlewareOnResponse(
		response: Response,
		middlewareCtx: Api.MiddlewareCallbackParams,
	): Promise<Response> {
		for (const middleware of this.middlewares) {
			const result = await middleware.onResponse?.({
				...middlewareCtx,
				response,
			});

			if (result) {
				if (!(result instanceof Response)) {
					throw new Error('onResponse: must return new Response() when modifying the response');
				}

				response = result;
			}
		}

		return response;
	}

}
